import { z } from 'zod';
import { NorthStarKpiSchema } from '../events/workspace.js';
import { MarketplaceOrderHandoffDefaultsSchema } from './marketplace-order-handoff-defaults.js';

export const CurrencyCodeEnum = z.enum(['USD', 'EUR', 'GBP', 'ILS', 'AUD', 'CAD', 'JPY']);
export type CurrencyCode = z.infer<typeof CurrencyCodeEnum>;

// ─── Tenant Settings ────────────────────────────────────────────────
// API-facing DTOs for tenant-level configuration.
// FE reads TenantSettingsResponse, submits UpdateTenantSettingsBody.

export const TenantSettingsResponseSchema = z.object({
  id: z.string(),
  tenantId: z.string(),

  conversionsMonthly: z.number().int(),
  conversionsWeekly: z.number().int(),

  defaultIndustry: z.string().nullable(),
  defaultProductsComp: z.string().nullable(),
  defaultProductsCore: z.string().nullable(),
  defaultAccount: z.string().nullable(),

  currency: CurrencyCodeEnum,
  defaultPlatforms: z.string().nullable(),
  defaultBrand: z.string().nullable(),

  pageSettings: z.record(z.string(), z.unknown()).nullable(),

  defaultNorthStar: NorthStarKpiSchema.nullable(),

  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TenantSettingsResponse = z.infer<typeof TenantSettingsResponseSchema>;

// Self-serve `PUT /tenant-settings` payload. Intentionally narrow — this is
// the only shape the public route accepts; anything else is silently stripped.
// Broader, admin-only fields live in AdminUpdateTenantSettingsBodySchema below.
export const UpdateTenantSettingsBodySchema = z.object({
  defaultBrand: z.string().max(255).nullish(),
  defaultIndustry: z.string().max(255).nullish(),
  currency: CurrencyCodeEnum.optional(),
});
export type UpdateTenantSettingsBody = z.infer<typeof UpdateTenantSettingsBodySchema>;

/** Cap for tenant Marketplace orders inbox addresses. Matches vendor contactEmails. */
export const MARKETPLACE_ORDERS_INBOX_EMAIL_COUNT_MAX = 10;

const marketplaceOrdersInboxEmailsSchema = z
  .array(z.string().trim().email().max(255))
  .max(MARKETPLACE_ORDERS_INBOX_EMAIL_COUNT_MAX)
  .optional();

/** Expand-phase: older clients may still PUT the scalar. Blank or null clears. */
const marketplaceOrdersInboxEmailLegacySchema = z.preprocess(
  (value) => {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
  },
  z.string().email().max(255).nullable().optional(),
);

const INDUSTRY_DEFAULT_BRANDS_COUNT_MAX = 500;

/** One admin-set My Brand per industry. `null` clears that industry's default. */
export const IndustryDefaultBrandUpdateSchema = z.object({
  industry: z.string().trim().min(1).max(255),
  defaultBrand: z.string().trim().min(1).max(255).nullable(),
});
export type IndustryDefaultBrandUpdate = z.infer<typeof IndustryDefaultBrandUpdateSchema>;

/** Opportunity types an org can create new sets of. Creation only — existing sets stay usable. */
export const OPPORTUNITY_GENERATION_TYPES = ['PERSONA', 'COMPETITOR', 'USER_GENERATED', 'POST_FLIGHT'] as const;
export const OpportunityGenerationTypeSchema = z.enum(OPPORTUNITY_GENERATION_TYPES);
export type OpportunityGenerationType = z.infer<typeof OpportunityGenerationTypeSchema>;

export const EnabledOpportunityTypesSchema = z.object({
  PERSONA: z.boolean(),
  COMPETITOR: z.boolean(),
  USER_GENERATED: z.boolean(),
  POST_FLIGHT: z.boolean(),
});
export type EnabledOpportunityTypes = z.infer<typeof EnabledOpportunityTypesSchema>;

/** Missing settings, or a column not yet written, means the type stays creatable. */
export const ALL_OPPORTUNITY_TYPES_ENABLED: EnabledOpportunityTypes = {
  PERSONA: true,
  COMPETITOR: true,
  USER_GENERATED: true,
  POST_FLIGHT: true,
};

const OPPORTUNITY_TYPE_LABEL: Record<OpportunityGenerationType, string> = {
  PERSONA: 'Persona',
  COMPETITOR: 'Competitor',
  USER_GENERATED: 'Custom',
  POST_FLIGHT: 'Post-Flight',
};

export function enabledOpportunityTypesFromSettings(
  settings: {
    opportunityGenerationPersonaEnabled?: boolean | null;
    opportunityGenerationCompetitorEnabled?: boolean | null;
    opportunityGenerationUserGeneratedEnabled?: boolean | null;
    opportunityGenerationPostFlightEnabled?: boolean | null;
  } | null | undefined,
): EnabledOpportunityTypes {
  if (!settings) return { ...ALL_OPPORTUNITY_TYPES_ENABLED };
  return {
    PERSONA: settings.opportunityGenerationPersonaEnabled ?? true,
    COMPETITOR: settings.opportunityGenerationCompetitorEnabled ?? true,
    USER_GENERATED: settings.opportunityGenerationUserGeneratedEnabled ?? true,
    POST_FLIGHT: settings.opportunityGenerationPostFlightEnabled ?? true,
  };
}

export function opportunityGenerationColumns(
  types: EnabledOpportunityTypes,
): {
  opportunityGenerationPersonaEnabled: boolean;
  opportunityGenerationCompetitorEnabled: boolean;
  opportunityGenerationUserGeneratedEnabled: boolean;
  opportunityGenerationPostFlightEnabled: boolean;
} {
  return {
    opportunityGenerationPersonaEnabled: types.PERSONA,
    opportunityGenerationCompetitorEnabled: types.COMPETITOR,
    opportunityGenerationUserGeneratedEnabled: types.USER_GENERATED,
    opportunityGenerationPostFlightEnabled: types.POST_FLIGHT,
  };
}

export function opportunityTypeGenerationDisabledMessage(type: OpportunityGenerationType): string {
  return `${OPPORTUNITY_TYPE_LABEL[type]} opportunity sets are turned off for this organization.`;
}

export function sameEnabledOpportunityTypes(a: EnabledOpportunityTypes, b: EnabledOpportunityTypes): boolean {
  return OPPORTUNITY_GENERATION_TYPES.every((type) => a[type] === b[type]);
}

// Admin-only `PUT /tenants/:tenantId/settings` payload. Superset of the
// self-serve fields plus tenant-provisioning knobs (conversions, north-star,
// customer partition) that only privileged admin flows may set.
export const AdminUpdateTenantSettingsBodySchema = z.object({
  defaultIndustry: z.string().max(255).nullable().optional(),
  defaultBrand: z.string().max(255).nullable().optional(),
  customerPartition: z.string().max(255).nullable().optional(),
  // Opts the tenant out of the cross-tenant BQ client guard (AD-2236). Admin-only
  // by construction: it is the sanctioned way to express deliberate sharing, so a
  // borrowed customerPartition stays distinguishable from a typo.
  isBqClientSharingApproved: z.boolean().optional(),
  conversionsMonthly: z.number().int().nonnegative().optional(),
  conversionsWeekly: z.number().int().nonnegative().optional(),
  currency: CurrencyCodeEnum.optional(),
  defaultNorthStar: NorthStarKpiSchema.nullable().optional(),
  // Empty array means "fall back to the ordering user". The route also writes
  // marketplaceOrdersInboxEmail as emails[0] ?? null (expand / write-both).
  marketplaceOrdersInboxEmails: marketplaceOrdersInboxEmailsSchema,
  marketplaceOrdersInboxEmail: marketplaceOrdersInboxEmailLegacySchema,
  marketplaceOrderHandoffDefaults: MarketplaceOrderHandoffDefaultsSchema.optional(),
  // Stored on tenant_competitor_settings, not tenant_settings. Listed industries
  // are written; omitted industries keep their current default.
  industryDefaultBrands: z
    .array(IndustryDefaultBrandUpdateSchema)
    .max(INDUSTRY_DEFAULT_BRANDS_COUNT_MAX)
    .optional(),
  bqAutoSyncEnabled: z.boolean().optional(),
  bqAutoSyncTimeUtc: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .optional(),
  bqSyncFailureNotifyUserId: z.string().cuid().nullable().optional(),
  bqSyncFailureSlackAlertEnabled: z.boolean().optional(),
  enabledOpportunityTypes: EnabledOpportunityTypesSchema.optional(),
});
export type AdminUpdateTenantSettingsBody = z.infer<typeof AdminUpdateTenantSettingsBodySchema>;

/** Canonical array for admin PUT. Array wins; omitted fields leave inbox unchanged. */
export function resolveAdminInboxEmailsUpdate(body: AdminUpdateTenantSettingsBody): string[] | undefined {
  if (body.marketplaceOrdersInboxEmails !== undefined) return body.marketplaceOrdersInboxEmails;
  if (body.marketplaceOrdersInboxEmail !== undefined) {
    return body.marketplaceOrdersInboxEmail ? [body.marketplaceOrdersInboxEmail] : [];
  }
  return undefined;
}
